import { debounce, isEmpty, isNil, map, omit, toPairs } from 'lodash';

import { SigFieldKeys, SigStatus } from 'enums/SigManagement.enum';
import { sigActions } from 'store/slices/sigSlice';
import {
  listAllDirectionsForSigApi,
  listApprovedDrugsForSigApi,
  listSigAssociationsApi,
  upsertSigAssociationsApi,
} from 'api/sig';

import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';
import { describeStagedChanges } from 'modules/SigManagement/utils/applySigDraft';

import { handleError } from './errorHandlerThunks';

// The status filter is about the sig, not its directions. SigAssociationFilter has
// The list filters on the sig's own status server-side. `isActive` in the same filter means
// the DIRECTION, which is why the two are named apart.
const toServerFilter = (filter) => {
  const status = filter?.status;

  if (isNil(status) || status === SigStatus.ALL) {
    return omit(filter, SigFieldKeys.STATUS);
  }

  return { ...omit(filter, SigFieldKeys.STATUS), sigStatus: status === SigStatus.ACTIVE };
};

export const loadSigAssociations =
  ({ page }) =>
  (dispatch, getState) => {
    const { sortBy, filter } = getState().sigs;
    dispatch(sigActions.updateSigsCurrentPage({ page }));

    return listSigAssociationsApi({
      page,
      sortBy,
      filter: toServerFilter(filter),
    })
      .then(({ count, result }) => {
        dispatch(sigActions.loadSigs({ page, items: result, count }));
      })
      .catch((error) => dispatch(handleError({ error })));
  };

export const debouncedLoadSigAssociations = debounce(({ dispatch }) => {
  return dispatch(loadSigAssociations({ page: 1 })).finally(() =>
    dispatch(sigActions.setSigListLoading(false))
  );
}, 1000);

export const updateSigAssociationsFilter =
  ({ filter }) =>
  (dispatch) => {
    dispatch(sigActions.setSigListLoading(true));
    dispatch(sigActions.updateSigsFilter({ filter }));
    debouncedLoadSigAssociations({ dispatch });
  };

export const updateSigAssociationsSortBy =
  ({ sortBy }) =>
  (dispatch) => {
    // No setSigListLoading here, unlike the filter path beside it. `sigs.loading` is read by the
    // Filters popover's Apply button and by nothing else — the table's own spinner keys off having
    // no rows for the page, which emptying `pages` below already produces. Setting it from a sort
    // put a spinner on the Apply button of a popover the user had not opened.
    dispatch(sigActions.updateSigsSortBy({ sortBy }));
    debouncedLoadSigAssociations({ dispatch });
  };

// The two halves of the detail dialog. Fetched together because the dialog shows both at once, and
// in parallel because neither depends on the other — they are on different databases.
export const loadSigDetail =
  ({ sigId }) =>
  (dispatch, getState) => {
    dispatch(sigActions.startSigDetailLoad({ sigId }));

    /**
     * Whether this request is still the one being awaited.
     *
     * Nothing cancels an in-flight fetch, and a sig with several pages of directions takes several
     * round trips. Open sig A, then open sig B before A finishes, and A's late response would write
     * `sigId: A` over B's already-loaded state — after which the dialog's own
     * `sigDetail.sigId === sigId` check reads false, so B renders as loading for ever while its
     * effect, keyed on an unchanged `sigId`, never fires again.
     *
     * `startSigDetailLoad` stamps the store with the sig being requested, so the store itself is
     * the record of which request is current. A stale response finds someone else's id there and
     * drops its own result.
     */
    const isStillTheCurrentRequest = () => getState().sigs.sigDetail.sigId === sigId;

    // Every direction, not the first page: the Deactivate button submits this list, and
    // upsertSigAssociations sets inactive whatever is missing from it.
    return Promise.all([
      listAllDirectionsForSigApi({ sigId }),
      // Inactive approvals included: this view is where they are made active again, and one it
      // cannot show is one nobody can put back.
      listApprovedDrugsForSigApi({ sigId, includeRetired: true }),
    ])
      .then(([directions, approvedDrugs]) => {
        if (!isStillTheCurrentRequest()) {
          return;
        }

        dispatch(
          sigActions.loadSigDetail({
            sigId,
            directions: directions.result,
            directionCount: directions.count,
            approvedDrugs: approvedDrugs.result,
            approvedDrugCount: approvedDrugs.count,
          })
        );
      })
      .catch((error) => {
        // The error is surfaced either way — the user asked for this sig at some point — but the
        // spinner is only cleared when the failure belongs to what is on screen now.
        if (isStillTheCurrentRequest()) {
          dispatch(sigActions.failSigDetailLoad());
        }

        return dispatch(handleError({ error }));
      });
  };

export const clearSigDetail = () => (dispatch) => dispatch(sigActions.clearSigDetail());

/**
 * Whether the draft holds anything a commit would actually write.
 *
 * Delegates to `describeStagedChanges` rather than testing the draft's maps for emptiness. Those
 * maps keep an entry after a status is toggled and toggled back, so asking whether they are empty
 * said "yes, there are changes" while the pending-changes card — which compares each entry against
 * the server's value — correctly said there were none. One definition of "a change", so the button
 * and the list it summarises cannot disagree.
 */
export const hasStagedSigChanges = ({ sig, sigDetail, sigDraft }) =>
  !isEmpty(
    describeStagedChanges({
      sig,
      directions: sigDetail?.directions ?? [],
      catalogItems: sigDetail?.approvedDrugs ?? [],
      sigDraft,
    })
  );

/**
 * Turns the staged draft into one mutation input.
 *
 * The two halves are shaped differently on purpose, because the server treats them differently:
 *
 * - `associatedDirections` is the sig's COMPLETE direction list in the state it should end up in.
 *   The server retires any direction absent from it, so a partial list is destructive. This is why
 *   the loaded directions are all rebuilt here rather than only the edited ones.
 * - `associatedCatalogItems` is a CHANGESET. Only what the user touched is named, and an approval
 *   not named is left alone. Sending the loaded page as a desired state would retire every approval
 *   past the first page.
 */
export const buildSigCommitPayload = ({ sigRow, sigDetail, sigDraft }) => {
  const directions = [
    ...map(sigDetail.directions, ({ directionId, direction, isActive }) => ({
      directionId,
      direction,
      directionStatus: sigDraft.directionStatusEdits[directionId] ?? isActive,
    })),
    ...map(sigDraft.newDirections, ({ direction }) => ({ direction, directionStatus: true })),
  ];

  // A status the server already has is not a change. Left in, it travelled as a catalog entry the
  // server would write over itself — bumping modified_timestamp on an approval nobody touched, and
  // reporting a changedCount the user could not account for.
  const serverStatusByCatalogId = new Map(
    map(sigDetail.approvedDrugs, ({ catalogId, isActive }) => [catalogId, isActive])
  );

  const catalogItems = [
    ...toPairs(sigDraft.catalogStatusEdits)
      .filter(([catalogId, isActive]) => serverStatusByCatalogId.get(catalogId) !== isActive)
      .map(([catalogId, isActive]) => ({ catalogId, isActive })),
    ...map(sigDraft.newCatalogItems, ({ catalogId }) => ({ catalogId, isActive: true })),
  ];

  return {
    sigId: sigRow.sigId,
    // The staged wording when there is one. The server treats a changed sig text as an identity
    // change and re-checks it for a canonical collision, so this must be the edited value rather
    // than the loaded one.
    //
    // Trimmed, because the two ends measured different things. The editor's length check is on the
    // trimmed text and the server's maximum is on the raw text — it has to be, the raw text is what
    // reaches the column — so 180 characters with a trailing space passed the field and was refused
    // by the server, with no way for the user to see the space that did it.
    sig: isEmpty(sigDraft.sigText) ? sigRow.sig : sigDraft.sigText.trim(),
    sigStatus: sigRow.sigStatus,
    associatedDirections: directions,
    // Omitted rather than sent empty, so "the user changed no approvals" and "the user cleared
    // every approval" cannot look the same to the server.
    ...(!isEmpty(catalogItems) && { associatedCatalogItems: catalogItems }),
  };
};

/**
 * Reloads the page on screen after a write, keeping the filter and the sort where the user put them.
 *
 * The save used to call `updateSigAssociationsFilter({ dispatch })`, which destructures `filter` off
 * an object that has none — so every save wrote `undefined` over the filter and dropped the user
 * back to an unfiltered page 1. The rows changed, so the cache is dropped; the query the user asked
 * for did not, so it is not.
 */
export const reloadSigAssociations = () => (dispatch, getState) => {
  const { currentPage } = getState().sigs;

  dispatch(sigActions.invalidateSigPages());

  return dispatch(loadSigAssociations({ page: currentPage }));
};

/**
 * The one write behind every button in the Sig Management dialog.
 *
 * A failure is handed back rather than pushed into the global error dialog. Almost everything this
 * mutation refuses is something the user can fix in the form they are looking at — the sig text
 * collides, the direction belongs to another sig, a length is out of range — and `handleError`
 * throws the backend's message away: it keeps the message for three whitelisted codes only and
 * otherwise shows a mapped i18nCode or "Request failed. Please try again." So the reviewer read
 * "Request failed" while the server had said "This direction is already approved with a different
 * sig", and the global dialog closed the form they needed to correct.
 *
 * `getGraphQLErrorMessage` is what the rest of the app uses for exactly this — see
 * AddCatalogDialog, EditDefaultNdcDialog, SetPartnerNdcPreferenceDialog — and it prefers the
 * backend's own message, which is the only thing specific enough to name the offending sig.
 */
export const upsertSigAssociations =
  ({ input }) =>
  (dispatch) => {
    return upsertSigAssociationsApi({ input })
      .then((response) => {
        dispatch(reloadSigAssociations());

        return response;
      })
      .catch((error) => ({ success: false, error: getGraphQLErrorMessage(error) }));
  };

/**
 * Applies the staged changes.
 *
 * Refuses to run unless the store holds every direction for this sig. The payload's direction half
 * is a desired end state, so committing against a partial list would retire the directions that
 * were never loaded and report success.
 */
export const commitSigAssociations =
  ({ sigRow }) =>
  (dispatch, getState) => {
    const { sigDetail, sigDraft } = getState().sigs;

    if (
      sigDetail.sigId !== sigRow.sigId ||
      sigDetail.directions.length < sigDetail.directionCount
    ) {
      return Promise.resolve({ success: false, incompleteDirections: true });
    }

    return dispatch(
      upsertSigAssociations({ input: buildSigCommitPayload({ sigRow, sigDetail, sigDraft }) })
    ).then((response) => {
      // The draft is only cleared once the server has it. A half-applied commit keeps the staged
      // changes, so a retry is one button rather than re-entering the lot.
      if (response?.success) {
        dispatch(sigActions.clearSigDraft());
      }

      return response;
    });
  };

/**
 * The payload that creates a sig together with everything the user associated to it.
 *
 * Deliberately not `buildSigCommitPayload` with the id left off. That one sends the sig's direction
 * list as a desired end state, computed by laying the draft over the rows the server returned —
 * there are no such rows here, so everything comes from the draft and nothing can be read as
 * "absent, therefore set inactive".
 *
 * `sigId` is omitted rather than sent undefined: it is what the server routes on, and the create
 * path is chosen by its absence.
 */
export const buildSigCreatePayload = ({ sigDraft }) => {
  const catalogItems = map(sigDraft.newCatalogItems, ({ catalogId }) => ({
    catalogId,
    isActive: true,
  }));

  return {
    sig: sigDraft.sigText?.trim(),
    sigStatus: true,
    associatedDirections: map(sigDraft.newDirections, ({ direction }) => ({
      direction,
      directionStatus: true,
    })),
    // Omitted rather than sent empty, matching the update path: an empty list and no list mean
    // different things to the server, and a sig created with no catalog approvals means the latter.
    ...(!isEmpty(catalogItems) && { associatedCatalogItems: catalogItems }),
  };
};

/**
 * Creates the sig the user composed, with its directions and catalog approvals, in one call.
 *
 * One mutation because it is one act. Creating the sig first and then associating to it would leave
 * a sig with no directions behind whenever the second call failed, and there is no screen that
 * would show the user they had made one.
 */
export const createSigWithAssociations = () => (dispatch, getState) => {
  const { sigDraft } = getState().sigs;

  return dispatch(upsertSigAssociations({ input: buildSigCreatePayload({ sigDraft }) })).then(
    (response) => {
      if (response?.success) {
        dispatch(sigActions.clearSigDraft());
      }

      return response;
    }
  );
};
